import { Injectable } from "@angular/core";
import { SnackbarConfig } from "app/common";
import { Statement } from "app/statements";
import { ErrorMessage, ErrorMessageContent } from "app/core/form-validator/validatorInterface";
import { StatementService } from "app/statements/statement.service";
import { Store } from "@ngrx/store";
import { State } from "app/reducers";
import { ValidateFieldV2Service } from "app/core/form-validator/validate-field-v2.service";
import { FreelanceJobSubSteps } from "app/core/workflow/steps-controller/sub-steps/additional-income/freelance-job.sub-steps";
import { CryptocurrencySubSteps } from "app/core/workflow/steps-controller/sub-steps/additional-income/cryptocurrency.sub-steps";
import {
  PensionFromList,
  PensionIncomeErrors,
  pensionList2019,
  pensionListBefore2019
} from "app/modules/statement/models/additional-income/pension-income.model";
import { DateHelper } from "app/core/helpers/date.helper";
import { PensionSubSteps } from "app/core/workflow/steps-controller/sub-steps/additional-income/pension.sub-steps";
import { SocialSubSteps } from "app/core/workflow/steps-controller/sub-steps/additional-income/social-sub.steps";
import { FreelanceJobManager } from "app/modules/statement/models/additional-income/freelance-job.manager";
import { CryptocurrencyIncomeManager } from "app/modules/statement/models/additional-income/cryptocurrency.manager";
import { VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME } from "app/core/form-validator/validator-field-names/validator-field-names-additional-income";
import { PensionIncomeManager } from "app/modules/statement/models/additional-income/pension-income.manager";
import { SocialIncomeManager } from "app/modules/statement/models/additional-income/social-income.manager";
import { finalize, take } from "rxjs/operators";
import { SHOW_SNACKBAR } from "app/reducers/actions/snackbar";
import { PensionIncomeTypes } from "app/modules/statement/models/additional-income/pension-income.types";
import { SocialIncomeErrors } from "app/modules/statement/models/additional-income/social-income.model";
import { FreelanceJobErrors } from "app/modules/statement/models/additional-income/freelance-job.model";
import { CryptocurrencyIncomeErrors } from "app/modules/statement/models/additional-income/cryptocurrency.model";
import { AdditionalIncomeConfig } from "app/configs/statement/additional-income.config";
import { FreelanceJobTypes } from "app/modules/statement/models/additional-income/freelance-job.types";
import { SocialIncomeTypes } from "app/modules/statement/models/additional-income/social-income.types";
import { CryptocurrencyIncomeTypes } from "app/modules/statement/models/additional-income/cryptocurrency.types";
import { UPDATE_REFUND_DETAILS } from "app/reducers/actions/refund-details";
import { BehaviorSubject, Subject } from "rxjs";
import { VatDeclarationSubSteps } from "../../../core/workflow/steps-controller/sub-steps/additional-income/vat-declaration.sub-steps";
import { VatDeclarationManager } from "../models/additional-income/vat-declaration.manager";
import { VatDeclarationErrors } from "../models/additional-income/vat-declaration.model";
import { declarationTypes } from "../components/additional-income-form/additional-income-form-vat/additional-income-form-vat.component";
import { TranslateService } from "@ngx-translate/core";
import { WorkflowControllerService } from "../../../core/workflow/workflow-controller/workflow-controller.service";

@Injectable()
export class AdditionalIncomeService {
  public originalStatement: Statement;
  public mainStatement: Statement;
  public currentStatement: Statement;
  public epValidator: ErrorMessage[];
  public disabilityLevels = [];
  public loading = false;
  public readonly inputIdBase = "additional-income-input__";
  public readonly VALIDATOR_KEYS = VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME;
  public readonly dateHelper = DateHelper;
  private ngUnsubscribe: Subject<void>;
  private _errorsSubject = new BehaviorSubject<any[]>([]);
  public errors$ = this._errorsSubject.asObservable();

  set errors(val: any[] | null | undefined) {
    if (this.isValidationErrorList(val)) {
      const translated = this.translateErrors(val);
      this._errorsSubject.next(this.dedupeErrors(translated));
      return;
    }

    if (!val) {
      this._errorsSubject.next([]);
      return;
    }

    this._errorsSubject.next(this.flattenBackendErrors(val));
  }

  get errors() {
    return this._errorsSubject.value;
  }

  refreshErrors() {
    this._errorsSubject.next(Array.isArray(this._errorsSubject.value) ? [...this._errorsSubject.value] : []);
  }

  private translateErrors(errors: ErrorMessage[]): ErrorMessage[] {
    if (!errors) return [];

    return errors.map((err) => ({
      ...err,
      messages: (err.messages || []).map((m) => {
        const content = typeof m === "string" ? { message: m } : m || { message: "" };

        return {
          ...content,
          message: this.translateMessage(content.message)
        };
      })
    }));
  }

  private translateMessage(message: string): string {
    if (typeof message !== "string") return message;

    // messages prefixed with "_" are ready to display texts (e.g. messages sent by the backend)
    if (message.charAt(0) === "_") {
      return this.translate.instant(message.substring(1));
    }

    return this.translate.instant(`EP_VALIDATOR.${message}`);
  }

  private dedupeErrors(errors: ErrorMessage[]): ErrorMessage[] {
    const map = new Map();

    errors.forEach((err) => {
      const key = `${err.fieldName}::${err.index}::${JSON.stringify(err.messages)}`;
      if (!map.has(key)) {
        map.set(key, err);
      }
    });

    return Array.from(map.values());
  }

  private isValidationErrorList(val: any): val is ErrorMessage[] {
    return Array.isArray(val) && val.every((item) => item && typeof item === "object" && "fieldName" in item);
  }

  private flattenBackendErrors(val: any): ErrorMessage[] {
    const errors: ErrorMessage[] = [];
    this.collectBackendErrors(val, errors);
    return this.dedupeErrors(errors);
  }

  private collectBackendErrors(val: any, errors: ErrorMessage[], index = 0): void {
    if (!val) {
      return;
    }

    if (Array.isArray(val)) {
      if (val.every((item) => typeof item === "string")) {
        this.pushBackendError(errors, "non_field_errors", index, val as string[]);
        return;
      }

      val.forEach((item, itemIndex) => this.collectBackendErrors(item, errors, itemIndex));
      return;
    }

    if (typeof val !== "object") {
      return;
    }

    Object.entries(val).forEach(([fieldName, fieldValue]) => {
      if (fieldValue == null) {
        return;
      }

      if (typeof fieldValue === "string") {
        this.pushBackendError(errors, fieldName, index, [fieldValue]);
        return;
      }

      if (Array.isArray(fieldValue)) {
        if (fieldValue.every((item) => typeof item === "string")) {
          this.pushBackendError(errors, fieldName, index, fieldValue as string[]);
          return;
        }

        if (fieldName === "freelance_jobs") {
          this.collectFreelanceJobBackendErrors(fieldValue, errors);
          return;
        }

        fieldValue.forEach((item, itemIndex) => this.collectBackendErrors(item, errors, itemIndex));
        return;
      }

      this.collectBackendErrors(fieldValue, errors, index);
    });
  }

  private pushBackendError(errors: ErrorMessage[], fieldName: string, index: number, messages: string[]): void {
    this.getBackendErrorFieldNames(fieldName).forEach((errorFieldName) => {
      const errorMessage = new ErrorMessage();
      errorMessage.fieldName = errorFieldName;
      errorMessage.index = index;
      errorMessage.messages = messages.map((message) => new ErrorMessageContent(this.normalizeBackendMessage(message)));
      errors.push(errorMessage);
    });
  }

  private collectFreelanceJobBackendErrors(items: any[], errors: ErrorMessage[]): void {
    items.forEach((item, itemIndex) =>
      this.collectBackendErrors(item, errors, this.getFreelanceJobLocalErrorIndex(itemIndex))
    );
  }

  private getFreelanceJobLocalErrorIndex(payloadIndex: number): number {
    const jobs = this.currentStatement?.freelanceJobs;
    const job = Array.isArray(jobs) ? jobs[payloadIndex] : null;
    const incomeType = job?.incomeType;

    if (!Array.isArray(jobs) || !job || !incomeType) {
      return payloadIndex;
    }

    return jobs.slice(0, payloadIndex + 1).filter((item) => item?.incomeType === incomeType).length - 1;
  }

  /**
   * Backend field name -> validator field names to publish the error under.
   *
   * app-validator-error-message is bound to VALIDATOR_KEYS values (e.g. ai_city, ai_freelance_name),
   * while getError() reads `ai_${field}` (e.g. ai_business_city, ai_name) for the form input -
   * publish under both when the mapped key differs, so both channels get the message.
   */
  private getBackendErrorFieldNames(fieldName: string): string[] {
    if (fieldName === "steuernummer") {
      return ["ai_steuernummer", "ai_business_steuernummer"];
    }

    const validatorFieldName =
      this.VALIDATOR_KEYS[fieldName] || this.VALIDATOR_KEYS[`freelance_${fieldName}`] || `ai_${fieldName}`;

    return validatorFieldName === `ai_${fieldName}` ? [validatorFieldName] : [validatorFieldName, `ai_${fieldName}`];
  }

  private normalizeBackendMessage(message: string): string {
    return message.startsWith("_") ? message : `_${message}`;
  }

  constructor(
    private store: Store<State>,
    private statementService: StatementService,
    private validateFieldV2Service: ValidateFieldV2Service,
    private translate: TranslateService,
    public workFlowController: WorkflowControllerService
  ) {}

  public _init(
    _mainStatement: Statement,
    _currentStatement: Statement,
    _ngUnsubscribe: Subject<void>,
    _originalStatement?: Statement
  ): void {
    this.resetInitialVariables();

    if (_originalStatement) {
      this.originalStatement = _originalStatement;
    } else {
      this.originalStatement = _mainStatement.clone();
    }

    this.mainStatement = _mainStatement;
    this.currentStatement = _currentStatement;

    this.ngUnsubscribe = _ngUnsubscribe;

    this.validateFieldV2Service._init(this.mainStatement);
  }

  public get isLoading(): boolean {
    return this.loading;
  }

  public clearErrors(): void {
    this.errors = [];
    this.epValidator = [];
  }

  // public getError(key: string, index = 0): string[] {
  //   if (this.errors && this.errors[index] && Array.isArray(this.errors[index][key])) {
  //     return this.errors[index][key];
  //   }
  //
  //   return [];
  // }
  getError(key: string, index: number = 0): string[] {
    const errors = this._errorsSubject.value || [];

    return errors
      .filter((e) => e.fieldName === `ai_${key}` && e.index === index)
      .flatMap((e) => (e.messages || []).map((m) => this.unwrapMessage(m)))
      .filter(Boolean);
  }

  private unwrapMessage(message: ErrorMessageContent | string): string {
    const value = typeof message === "string" ? message : message?.message;

    if (!value) {
      return "";
    }

    return value.startsWith("_") ? value.substring(1) : value;
  }

  public pensionNamesList(): PensionFromList[] {
    if (this.mainStatement.fullYear >= 2019) {
      return pensionList2019;
    }

    return pensionListBefore2019;
  }

  public get socialIncomeLimit(): number {
    return AdditionalIncomeConfig.ITEMS_LIMIT_IN_SUBSECTION_SOCIAL_INCOME;
  }

  public get pensionIncomeLimit(): number {
    return AdditionalIncomeConfig.ITEMS_LIMIT_IN_SUBSECTION_PENSION_INCOME;
  }

  public get freelanceIncomeLimit(): number {
    return AdditionalIncomeConfig.ITEMS_LIMIT_IN_SUBSECTION_FREELANCE_INCOME;
  }

  public get cryptocurrencyIncomeLimit(): number {
    return AdditionalIncomeConfig.ITEMS_LIMIT_IN_SUBSECTION_CRYPTOCURRENCY_INCOME;
  }

  public setFromOriginalStatement(): void {
    Object.assign(this.mainStatement, this.originalStatement.clone());

    this.updateAssignCurrentStatement();
  }

  public updateOriginalStatement(): void {
    Object.assign(this.originalStatement, this.mainStatement.clone());
  }

  public updateAdditionalIncome(
    group: SocialSubSteps | PensionSubSteps | FreelanceJobSubSteps | CryptocurrencySubSteps | VatDeclarationSubSteps,
    callbackSuccess: (result: boolean) => void,
    assignResponse = true,
    includeVatDeclaration = true
  ): void {
    this.clearErrors();
    console.log("updateAdditionalIncome");
    const isSpouse = this.currentStatement.isSpouse();
    const preserveVatDeclaration =
      !includeVatDeclaration &&
      [FreelanceJobSubSteps.FREELANCE, FreelanceJobSubSteps.GEWERBE].includes(group as FreelanceJobSubSteps) &&
      !!this.currentStatement?.vatDeclaration &&
      !!(this.currentStatement.vatDeclaration as any).declaration_type;

    const payload = !isSpouse
      ? this.mainStatement.additionalIncomeApplicantPayload("", group, includeVatDeclaration)
      : this.mainStatement.additionalIncomeSpousePayload("", group, includeVatDeclaration);

    this.statementService
      .updateStatement(this.mainStatement.id, payload)
      .pipe(
        take(1),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (response: Statement) => {
          const backendVatDeclaration = (response as any).vat_declaration;
          const currentVatDeclaration = this.currentStatement.vatDeclaration;

          if (assignResponse) {
            const responseToAssign: any = { ...response };

            if (preserveVatDeclaration && backendVatDeclaration == null && currentVatDeclaration) {
              if (isSpouse) {
                responseToAssign.spouse = {
                  ...(responseToAssign.spouse || {}),
                  vatDeclaration: currentVatDeclaration
                };
              } else {
                responseToAssign.vatDeclaration = currentVatDeclaration;
              }
            }

            Object.assign(this.mainStatement, responseToAssign);

            this.updateAssignCurrentStatement();
          }

          this.updateOriginalStatement();

          this.store.dispatch({ type: UPDATE_REFUND_DETAILS });

          this.workFlowController.reInit();

          callbackSuccess(true);
        },
        error: (errors) => {
          if (errors.error) {
            const backendErrors = this.flattenBackendErrors(errors.error);
            // epValidator is the channel used by app-validator-error-message (fields rendering);
            // ai_detail / ai_non_field_errors never reach a field, so they fall back to the global snackbar
            const fieldErrors = backendErrors.filter(
              (error) => error.fieldName !== "ai_detail" && error.fieldName !== "ai_non_field_errors"
            );

            if (fieldErrors.length) {
              this.errors = errors.error;
              this.epValidator = fieldErrors;
            } else {
              this.showGlobalError();
            }
          } else {
            this.showGlobalError();
          }
          callbackSuccess(false);
        }
      });
  }

  public isSocialIncomeSection(
    sectionName:
      | SocialSubSteps
      | PensionSubSteps
      | FreelanceJobSubSteps
      | CryptocurrencySubSteps
      | FreelanceJobTypes
      | PensionIncomeTypes
      | SocialIncomeTypes
      | CryptocurrencyIncomeTypes
      | VatDeclarationSubSteps
  ): boolean {
    return (
      Object.values(SocialSubSteps).includes(sectionName as SocialSubSteps) ||
      Object.values(SocialIncomeTypes).includes(sectionName as SocialIncomeTypes)
    );
  }

  public isPensionIncomeSection(
    sectionName:
      | SocialSubSteps
      | PensionSubSteps
      | FreelanceJobSubSteps
      | CryptocurrencySubSteps
      | FreelanceJobTypes
      | PensionIncomeTypes
      | SocialIncomeTypes
      | CryptocurrencyIncomeTypes
      | VatDeclarationSubSteps
  ): boolean {
    return (
      Object.values(PensionSubSteps).includes(sectionName as PensionSubSteps) ||
      Object.values(PensionIncomeTypes).includes(sectionName as PensionIncomeTypes)
    );
  }

  public isFreelanceIncomeSection(
    sectionName:
      | SocialSubSteps
      | PensionSubSteps
      | FreelanceJobSubSteps
      | CryptocurrencySubSteps
      | FreelanceJobTypes
      | PensionIncomeTypes
      | SocialIncomeTypes
      | CryptocurrencyIncomeTypes
      | VatDeclarationSubSteps
  ): boolean {
    return (
      Object.values(FreelanceJobSubSteps).includes(sectionName as FreelanceJobSubSteps) ||
      Object.values(FreelanceJobTypes).includes(sectionName as FreelanceJobTypes)
    );
  }

  public isCryptocurrencyIncomeSection(
    sectionName:
      | SocialSubSteps
      | PensionSubSteps
      | FreelanceJobSubSteps
      | CryptocurrencySubSteps
      | FreelanceJobTypes
      | PensionIncomeTypes
      | SocialIncomeTypes
      | CryptocurrencyIncomeTypes
      | VatDeclarationSubSteps
  ): boolean {
    return (
      Object.values(CryptocurrencySubSteps).includes(sectionName as CryptocurrencySubSteps) ||
      Object.values(CryptocurrencyIncomeTypes).includes(sectionName as CryptocurrencyIncomeTypes)
    );
  }

  public isVatDeclarationSection(
    sectionName:
      | SocialSubSteps
      | PensionSubSteps
      | FreelanceJobSubSteps
      | CryptocurrencySubSteps
      | FreelanceJobTypes
      | PensionIncomeTypes
      | SocialIncomeTypes
      | CryptocurrencyIncomeTypes
      | VatDeclarationSubSteps
  ): boolean {
    return Object.values(VatDeclarationSubSteps).includes(sectionName as VatDeclarationSubSteps);
  }

  public checkAdditionalIncomeFields(): void {}

  public formValidationErrors(
    group: SocialSubSteps | PensionSubSteps | FreelanceJobSubSteps | CryptocurrencySubSteps | VatDeclarationSubSteps
  ): void {
    this.checkAdditionalIncomeFields();

    this.epValidator = [];

    let errorMessages: Array<ErrorMessage> = [];

    if (!this.mainStatement) {
      return;
    }

    let dataForValidation,
      validationFieldKeys = [];

    switch (group) {
      case SocialSubSteps.UNEMPLOYMENT:
        dataForValidation = SocialIncomeManager.getUnemployment(this.currentStatement);
        break;
      case SocialSubSteps.PARENTAL:
        dataForValidation = SocialIncomeManager.getParental(this.currentStatement);
        break;
      case SocialSubSteps.INSOLVENCY:
        dataForValidation = SocialIncomeManager.getInsolvency(this.currentStatement);
        break;
      case SocialSubSteps.SICKNESS:
        dataForValidation = SocialIncomeManager.getSickness(this.currentStatement);
        break;
      case SocialSubSteps.MATERNITY:
        dataForValidation = SocialIncomeManager.getMaternity(this.currentStatement);
        break;
      case SocialSubSteps.INJURY:
        dataForValidation = SocialIncomeManager.getInjury(this.currentStatement);
        break;
      case SocialSubSteps.TRANSITION:
        dataForValidation = SocialIncomeManager.getTransition(this.currentStatement);
        break;
      case SocialSubSteps.OTHER:
        dataForValidation = SocialIncomeManager.getOther(this.currentStatement);
        break;
      case PensionSubSteps.PENSION:
        dataForValidation = PensionIncomeManager.getPension(this.currentStatement);
        break;
      case FreelanceJobSubSteps.FREELANCE:
        dataForValidation = FreelanceJobManager.getFreelance(this.currentStatement);
        break;
      case FreelanceJobSubSteps.GEWERBE:
        dataForValidation = FreelanceJobManager.getGewerbe(this.currentStatement);
        break;
      case FreelanceJobSubSteps.VOLUNTERING_INCOME:
        dataForValidation = FreelanceJobManager.getVolunteringIncome(this.currentStatement);
        break;
      case CryptocurrencySubSteps.CRYPTOCURRENCY:
        dataForValidation = CryptocurrencyIncomeManager.getCryptoCurrencyPension(this.currentStatement);
        break;
      case VatDeclarationSubSteps.VAT_DECLARATION:
        dataForValidation = VatDeclarationManager.getVatDeclaration(this.currentStatement);
        break;
    }

    if (this.isSocialIncomeSection(group)) {
      console.log(group);

      validationFieldKeys = [VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.social_amount];

      if (group === SocialSubSteps.OTHER) {
        validationFieldKeys.push(VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.social_other_name);
      }

      if (this.mainStatement.fullYear <= 2018) {
        validationFieldKeys.push(
          VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.social_period_lower,
          VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.social_period_upper
        );
      }
    }

    if (this.isPensionIncomeSection(group)) {
      this.currentStatement.pensionIncome.forEach((el) => {
        if (el.incomeType === PensionIncomeTypes.PENSION) {
          if (!el.adjustment) {
            el.adjustment = 0.0;
          }
        }
      });

      validationFieldKeys = [
        VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.pension_from,
        VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.pension_amount,
        VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.pension_start,
        VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.pension_adjustment
      ];
    }

    if (this.isFreelanceIncomeSection(group)) {
      if (group == FreelanceJobSubSteps.VOLUNTERING_INCOME) {
        validationFieldKeys = [
          VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.freelance_name,
          VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.freelance_income
          // VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.freelance_costs,
        ];
      } else {
        errorMessages = [...this.validateFreelanceAndGewerbe(dataForValidation)];
      }
    }

    if (this.isCryptocurrencyIncomeSection(group)) {
      validationFieldKeys = [VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.cryptocurrency_income_net];
    }

    if (this.isVatDeclarationSection(group)) {
      errorMessages = [...this.validateVatDeclaration(dataForValidation)];
    }

    //check if it has Freelance and Gewerbe validation
    const validateFieldAndGetMessage =
      errorMessages.length > 0
        ? [...errorMessages]
        : [...this.validateFieldV2Service.validateFieldAndGetMessageV2(dataForValidation, validationFieldKeys)];

    this.epValidator = [...this.epValidator, ...validateFieldAndGetMessage];

    console.log(this.epValidator);

    this.validateFreelanceAmount();
    this.errors = this.epValidator; // teraz errors$ emituje epValidator
  }

  public validateVatDeclaration(dataForValidation: any): Array<ErrorMessage> {
    let vatValidationMessage = [];
    let vatValidationFieldKeys = [];

    dataForValidation.forEach((data, index) => {
      const customErrorMessage = [];
      vatValidationFieldKeys =
        data.declaration_type === declarationTypes.BUSINESS
          ? [
              VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.income_sum,
              VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.last_year_income,
              VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.declaration_type
            ]
          : [
              VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.declaration_type,
              VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.type_of_taxation,
              VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.general_rate_net_revenues,
              VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.reduced_rate_net_revenues,
              VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.zero_percent_net_revenues,
              // VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.reverse_charge_first,
              // VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.reverse_charge_second,
              // VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.reverse_charge_third,
              VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.sales_value_settled_by_recipient,
              VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.vat_refund_value,
              VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.advance_payments
            ];

      vatValidationFieldKeys =
        data.premium_selected && data.declaration_type === declarationTypes.BUSINESS ? [] : vatValidationFieldKeys;

      if (data.reverse_charge) {
        vatValidationFieldKeys.push(
          VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.reverse_charge_first,
          VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.reverse_charge_second,
          VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.reverse_charge_third
        );
        if (data.reverse_charge_first) {
          vatValidationFieldKeys.push(VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.reverse_charge_first_vat_rate);
        }
        if (data.reverse_charge_second) {
          vatValidationFieldKeys.push(VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.reverse_charge_second_vat_rate);
        }
        if (data.reverse_charge_third) {
          vatValidationFieldKeys.push(VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.reverse_charge_third_vat_rate);
        }
      }

      if (data.has_steuernummer) {
        vatValidationFieldKeys.push(
          VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.tax_authority,
          VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.steuernummer
        );
      }

      vatValidationMessage = [
        ...customErrorMessage,
        ...vatValidationMessage,
        ...this.validateFieldV2Service.validateFieldAndGetMessageV2(data, vatValidationFieldKeys, index)
      ];
    });

    return vatValidationMessage;
  }

  public validateFreelanceAndGewerbe(dataForValidation: any): Array<ErrorMessage> {
    const checkIfItIs2020 = this.mainStatement.fullYear === 2020; //TODO: remove after 2 moths, it is temporary solution

    let freelanceValidationMessage = [];
    let freelanceValidationFieldKeys = [];

    dataForValidation.forEach((data, index) => {
      const customErrorMessage = [];
      freelanceValidationFieldKeys = checkIfItIs2020
        ? [VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.business_type]
        : [
            VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.total_payments,
            VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.total_withdrawals,
            VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.business_type,
            VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.has_sold_business_real_estate,
            VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.business_postal_code,
            VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.business_street,
            VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.business_city
          ];

      if (data.full_year) {
        freelanceValidationFieldKeys.push(
          VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.business_start,
          VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.business_end
        );
      }

      if (data.has_steuernummer) {
        freelanceValidationFieldKeys.push(
          VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.tax_authority,
          VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.business_steuernummer
        );
      }

      if (data.has_business_name) {
        freelanceValidationFieldKeys.push(VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.business_name);
      }

      if (data.has_corona) {
        freelanceValidationFieldKeys.push(VALIDATOR_FIELD_NAMES_ADDITIONAL_INCOME.corona_value);
      } else if (data.corona_value) {
        data.corona_value = null; //set value for null if has_corona is set to false and corona_value has some values
      }

      if (data.business_type === data.business_name) {
        //business_type and business_name can not have equal value TAX-3086
        customErrorMessage.push(
          this.addListEpValidatorMessage(this.VALIDATOR_KEYS.business_name, "BUSSINES_NAME_IS_EQUAL_TO_BUSINESS_TYPE")
        );
      }

      if (data.business_type === this.currentStatement.lastName) {
        //business_type and lastName can not have equal value TAX-3089
        customErrorMessage.push(
          this.addListEpValidatorMessage(this.VALIDATOR_KEYS.business_type, "BUSSINES_TYPE_IS_EQUAL_TO_LAST_NAME")
        );
      }

      freelanceValidationMessage = [
        ...customErrorMessage,
        ...freelanceValidationMessage,
        ...this.validateFieldV2Service.validateFieldAndGetMessageV2(data, freelanceValidationFieldKeys, index)
      ];
    });

    return freelanceValidationMessage;
  }

  private addListEpValidatorMessage(field_name: string, message: string): ErrorMessage {
    const errorMessage = new ErrorMessage();
    errorMessage.fieldName = field_name;
    errorMessage.index = 0;
    errorMessage.messages = [new ErrorMessageContent(message)];
    return errorMessage;
  }

  private validateFreelanceAmount(): void {
    const freelanceNetSum = FreelanceJobManager.getFreelance(this.currentStatement).reduce(
      (a, b) => a + Number(b.incomeNet),
      0
    );

    if (AdditionalIncomeConfig.FREELANCE_JOB_THRESHOLD <= freelanceNetSum) {
      FreelanceJobManager.getFreelance(this.currentStatement).forEach((el, i) => {
        this.addFreelanceAmountExceededError(i);
      });
    }

    const gewerbeNetSum = FreelanceJobManager.getGewerbe(this.currentStatement).reduce(
      (a, b) => a + Number(b.incomeNet),
      0
    );

    if (AdditionalIncomeConfig.FREELANCE_JOB_THRESHOLD <= gewerbeNetSum) {
      FreelanceJobManager.getGewerbe(this.currentStatement).forEach((el, i) => {
        this.addFreelanceAmountExceededError(i);
      });
    }
  }

  private addFreelanceAmountExceededError(index: number): void {
    const errorMessage = new ErrorMessage();
    errorMessage.fieldName = this.VALIDATOR_KEYS.freelance_income_net;
    errorMessage.index = index;
    errorMessage.messages = [
      new ErrorMessageContent("_ADDITIONAL_INCOME.FREELANCE_GROUP.ALERT.AMOUNT_EXCEEDED_ERROR", {
        amount: AdditionalIncomeConfig.FREELANCE_JOB_THRESHOLD
      })
    ];
    this.epValidator.push(errorMessage);
  }

  private showGlobalError(): void {
    this.store.dispatch({
      type: SHOW_SNACKBAR,
      payload: new SnackbarConfig("COMMON.GLOBAL_ERROR", "ERROR")
    });
  }

  private resetInitialVariables(): void {
    this.originalStatement = undefined;
    this.mainStatement = undefined;
    this.currentStatement = undefined;
    this.errors = undefined;
    this.epValidator = undefined;
    this.disabilityLevels = [];
    this.loading = false;
  }

  private updateAssignCurrentStatement(): void {
    if (this.currentStatement.isSpouse() && this.mainStatement.spouse) {
      Object.assign(this.currentStatement, this.mainStatement.spouse);
    } else {
      Object.assign(this.currentStatement, this.mainStatement);
    }
  }

  public getMainStatement() {
    return this.mainStatement;
  }
}
